import { ChevronLeft, ChevronRight } from 'components/icons';
import { Button } from 'components/ui/button';
import { Pagination, PaginationContent, PaginationItem } from 'components/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { FormattedMessage } from 'react-intl';
import { t } from 'utils/getTranslations';

interface Props {
    count: number;
    /** Zero-based. */
    page: number;
    rowsPerPage: number;
    onPageChange: (page: number) => void;
    /** The rows-per-page control shows only when options are given. */
    rowsPerPageOptions?: number[];
    onRowsPerPageChange?: (rowsPerPage: number) => void;
}

/** A table footer: the rows-per-page choice, the "X–Y of Z" range and previous/next page. */
export function ProcessesTablePagination({
    count,
    page,
    rowsPerPage,
    onPageChange,
    rowsPerPageOptions,
    onRowsPerPageChange,
}: Props) {
    const from = count === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min(count, (page + 1) * rowsPerPage);
    const isLastPage = (page + 1) * rowsPerPage >= count;

    return (
        <div className="text-body1 flex min-h-[52px] items-center justify-end pr-0.5 pl-4">
            {rowsPerPageOptions?.length && onRowsPerPageChange ? (
                <>
                    <span>{t('pagination.rows-per-page')}:</span>
                    <Select<number>
                        value={rowsPerPage}
                        onValueChange={(value) => value !== null && onRowsPerPageChange(value)}
                    >
                        <SelectTrigger
                            size="sm"
                            aria-label={t('pagination.rows-per-page')}
                            className="mr-8 ml-2 border-0 bg-transparent shadow-none"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {rowsPerPageOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                    {option}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </>
            ) : null}
            {/* The rows swap without the focused button's name changing, so this line is the only
                thing that says a page turned; `atomic` so the whole range is read, not one number. */}
            <span aria-live="polite" aria-atomic>
                <FormattedMessage id="pagination.range-of" values={{ from, to, count }} />
            </span>
            <Pagination className="mx-0 ml-5 w-auto" aria-label={t('pagination.pagination')}>
                <PaginationContent className="gap-0">
                    <PaginationItem>
                        <Button
                            variant="ghost"
                            size="icon-lg"
                            className="rounded-full"
                            aria-label={t('pagination.previous-page')}
                            disabled={page === 0}
                            onClick={() => onPageChange(page - 1)}
                        >
                            <ChevronLeft />
                        </Button>
                    </PaginationItem>
                    <PaginationItem>
                        <Button
                            variant="ghost"
                            size="icon-lg"
                            className="rounded-full"
                            aria-label={t('pagination.next-page')}
                            disabled={isLastPage}
                            onClick={() => onPageChange(page + 1)}
                        >
                            <ChevronRight />
                        </Button>
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        </div>
    );
}
